import { RoleName } from './types';

export default function access(
  initialState: { currentUser?: any } | undefined,
) {
  const { currentUser } = initialState ?? {};
  const roleName = currentUser?.role?.name;
  const additionalRoleNames: string[] = (
    currentUser?.additionalRoles ?? []
  ).map((r: any) => r.name);
  const isAdmin =
    roleName === RoleName.ADMIN || additionalRoleNames.includes(RoleName.ADMIN);

  // 按钮权限码列表（后端登录时返回）
  const buttonPermissions: string[] = currentUser?.permissions || [];
  // 菜单权限码列表
  const menuCodes: string[] = [];
  const extractMenuCodes = (menus: any[]) => {
    for (const m of menus) {
      if (m.code) menuCodes.push(m.code);
      if (m.children?.length) extractMenuCodes(m.children);
    }
  };
  extractMenuCodes(currentUser?.menus || []);

  const hasButton = (code: string) =>
    isAdmin || buttonPermissions.includes(code);
  const hasMenu = (code: string) => isAdmin || menuCodes.includes(code);

  // 构建 access 对象
  const accessMap: Record<string, boolean> = {
    canAdmin: isAdmin,
    messagesCenter:
      hasMenu('menu:notifications') || hasMenu('menu:oa:announcement'),
    // 菜单权限
    'menu:dashboard': true,
    'menu:product-initiation': hasMenu('menu:product-initiation'),
    'menu:requirement': hasMenu('menu:requirement'),
    'menu:task': hasMenu('menu:task'),
    'menu:issue': hasMenu('menu:issue'),
    'menu:notifications':
      hasMenu('menu:notifications') || hasMenu('menu:oa:announcement'),
    'menu:oa': hasMenu('menu:oa'),
    'menu:oa:announcement': hasMenu('menu:oa:announcement'),
    'menu:oa:document': hasMenu('menu:oa:document'),
    'menu:oa:report': hasMenu('menu:oa:report'),
    'menu:oa:approval': hasMenu('menu:oa:approval'),
    'menu:oa:asset': hasMenu('menu:oa:asset'),
    'menu:oa:discussion': hasMenu('menu:oa:discussion'),
    'menu:customer-follow-up': hasMenu('menu:customer-follow-up'),
    'menu:feedback': hasMenu('menu:feedback'),
    'menu:admin:feedback-config':
      isAdmin || hasMenu('menu:admin:feedback-config'),
    'btn:customer:claim': hasButton('btn:customer:claim'),
    'btn:customer:assign': hasButton('btn:customer:assign'),
    'menu:admin': isAdmin || hasMenu('menu:admin'),
    'menu:admin:user': isAdmin || hasMenu('menu:admin:user'),
    'menu:admin:role': isAdmin || hasMenu('menu:admin:role'),
    'menu:admin:stage': isAdmin || hasMenu('menu:admin:stage'),
    'menu:admin:department': isAdmin || hasMenu('menu:admin:department'),
    'menu:admin:permission': isAdmin || hasMenu('menu:admin:permission'),
    'menu:admin:audit-log': isAdmin || hasMenu('menu:admin:audit-log'),
    'menu:admin:backup-monitor':
      isAdmin || hasMenu('menu:admin:backup-monitor'),
    'menu:admin:requirement-types':
      isAdmin || hasMenu('menu:admin:requirement-types'),
    'menu:admin:device-models': isAdmin || hasMenu('menu:admin:device-models'),
    'menu:admin:wechat-test': isAdmin || hasMenu('menu:admin:wechat-test'),
    'menu:admin:notification-settings':
      isAdmin || hasMenu('menu:admin:notification-settings'),
    'menu:admin:dashboard-blocks':
      isAdmin || hasMenu('menu:admin:dashboard-blocks'),
    'menu:admin:approval-type': isAdmin || hasMenu('menu:admin:approval-type'),
    'menu:admin:data-dictionary':
      isAdmin ||
      hasMenu('menu:admin:data-dictionary') ||
      hasMenu('menu:admin:requirement-types') ||
      hasMenu('menu:admin:device-models'),
    'menu:admin:system-settings':
      isAdmin ||
      hasMenu('menu:admin:system-settings') ||
      hasMenu('menu:admin:user') ||
      hasMenu('menu:admin:role') ||
      hasMenu('menu:admin:department') ||
      hasMenu('menu:admin:permission') ||
      hasMenu('menu:admin:approval-type') ||
      hasMenu('menu:admin:feedback-config'),
  };

  // 动态注入所有按钮权限
  for (const code of buttonPermissions) {
    accessMap[code] = true;
  }

  // 向后兼容旧的 access 名称
  accessMap.canViewTask = hasMenu('menu:task');
  accessMap.canViewRequirements = hasMenu('menu:requirement');
  accessMap.canViewProductInit = hasMenu('menu:product-initiation');
  accessMap.canCreateProductInit = hasButton('btn:product-init:create');
  accessMap.canCreateRequirement = hasButton('btn:requirement:create');
  accessMap.canDeleteRequirement = hasButton('btn:requirement:delete');
  accessMap.canAssignRequirement = hasButton('btn:requirement:assign');

  return accessMap;
}
